import { IEntity, PhoneNumber } from "us.common/interfaces";
import dayjs from "dayjs";
import _ from "lodash";
import { appInsights } from "us.helper";
import { initPhoneNumberFields, SystemCountryCodeFormat } from "../Constants";
import { PhoneNumberTypes } from "us.common/constants";

export const formInitialValue = (
  entityDetails: IEntity,
  systemCountryCode: string
) => {
  try {
    return {
      ...entityDetails,
      ...getPhoneNoFields(entityDetails.phoneNumbers as Array<PhoneNumber>),
      personNo:
        entityDetails.debtorType === "P"
          ? entityDetails.idNo.trim() === ""
            ? ""
            : systemCountryCode?.toLowerCase() === SystemCountryCodeFormat.SE
            ? entityDetails.idNo
            : formatPersonNo(entityDetails.idNo)
          : "",
      companyNo:
        entityDetails.debtorType === "N"
          ? formatOrganizationNo(entityDetails.idNo)
          : "",
      countryCode: entityDetails.countryCode ?? systemCountryCode,
      addrUnknown:
        entityDetails.debtorType === "P"
          ? entityDetails.zipCode?.length !== 0
            ? false
            : true
          : entityDetails.zipCode?.length !== 0 &&
            entityDetails.zipName?.length !== 0
          ? false
          : true,
      postalAreaVisible: false,
      popConfirmVisible: false,
      isExistingPostalCode: entityDetails.isExistingPostalCode ?? true,
      isExistingMunicipalityCode:
        entityDetails.isExistingMunicipalityCode ?? true,
      systemCountryCode: systemCountryCode,
    };
  } catch (error) {
    appInsights.trackException(
      `Error in form initial values in Debtor Information. - ${error}`
    );
    return { ...entityDetails };
  }
};
/**
 * Person No format with space
 * @param value
 * @returns
 */
const formatPersonNo = (value: any) => {
  try {
    if (!value) {
      return value;
    }
    value = value.replace(/ /g, "");
    if (value.length === 6 || (value.length >= 9 && value.length <= 11)) {
      const parts = value.match(/.{1,6}/g);
      return parts.join(" ");
    } else {
      return value;
    }
  } catch (error) {
    appInsights.trackException(
      `Error in function of Person No format with space in Debtor Information. - ${error}`
    );
    return value;
  }
};
/**
 * Organization number format
 * @param value
 * @returns
 */
const formatOrganizationNo = (value: any) => {
  try {
    if (!value) {
      return "";
    }
    value = value.replace(/ /g, "");
    if (value.length === 9) {
      const parts = value.match(/.{1,3}/g);
      return parts.join(" ");
    } else {
      return value;
    }
  } catch (error) {
    appInsights.trackException(
      `Error in function of Organization number format in Debtor Information. - ${error}`
    );
    return value;
  }
};
/**
 * Validate person number
 * Also caters for Norwegian D-nummer, where the first digit of the
 * day is offset by +4 (day range 41-71 instead of 01-31)
 * @param personNumber person number
 * @returns {Object}
 */
export const validatePersonNo = (
  personNumber: number | string
): {
  isValid: boolean;
  birthDate: string;
  isDNumber: boolean;
} => {
  let response = {
    isValid: false,
    birthDate: "",
    isDNumber: false,
  };
  try {
    let personNo = personNumber.toString();
    let validLength = validatePersonNoLength(personNo);

    if (validLength.isVaild) {
      let bDate = generateBirthDate(personNo);
      if (validLength.numberLength >= 9) {
        response = {
          isValid:
            Boolean(+bDate?.date) &&
            bDate?.date.getDate() == bDate.day &&
            bDate.date.getMonth() + 1 == bDate.month,
          birthDate: bDate.date.toString(),
          isDNumber: bDate.isDNumber,
        };
      } else if (validLength.numberLength === 6) {
        response = {
          isValid:
            Boolean(+bDate?.date) &&
            bDate?.date.getDate() == bDate.day &&
            bDate.date.getMonth() + 1 == bDate.month,
          birthDate: bDate.date.toString(),
          isDNumber: bDate.isDNumber,
        };
      }
    }
    if (dayjs().endOf("day") < dayjs(response.birthDate) && response.isValid) {
      return {
        isValid: false,
        birthDate: "",
        isDNumber: false,
      };
    }
    return response;
  } catch (error) {
    appInsights.trackException(
      `Error in function of Validate person number in Debtor Information. - ${error}`
    );
    return response;
  }
};

/**
 * Generate birthdate accoding to the person number
 * Handles Norwegian D-nummer by detecting the +4 offset on the day
 * segment (41-71) and normalizing it back to a real calendar day (01-31)
 * @param personNo
 * @returns
 */
const generateBirthDate = (personNo: string) => {
  let rawDay = parseInt(personNo.substring(0, 2));
  let isDNumber = rawDay >= 41 && rawDay <= 71;
  let day = isDNumber ? rawDay - 40 : rawDay;
  let month = parseInt(personNo.substring(2, 4));
  let year = personNo.substring(4, 6);
  let date: Date = new Date(dayjs().format());
  let yearCode: number = 0;

  try {
    if (personNo.length >= 9) {
      let centryCode = personNo.substring(6, 9);
      if (parseInt(centryCode) >= 0 && parseInt(centryCode) <= 499) {
        yearCode = parseInt(`19${year}`);
      } else if (parseInt(centryCode) >= 500 && parseInt(centryCode) <= 999) {
        yearCode = parseInt(`20${year}`);
      }
      date = new Date(yearCode, +month - 1, day);
    } else if (personNo.length === 6) {
      let currentYear = new Date().getFullYear().toString().substring(0, 2);
      let assumeYear = parseInt(currentYear + year);
      if (assumeYear > new Date().getFullYear()) {
        assumeYear = assumeYear - 100;
      }
      date = new Date(assumeYear, +month - 1, day);
    }
    return {
      date,
      month,
      day,
      isDNumber,
    };
  } catch (error) {
    appInsights.trackException(
      `Error in function of generateBirthDate in Debtor Information. - ${error}`
    );
    return {
      date,
      month,
      day,
      isDNumber,
    };
  }
};

/**
 * person number length validation
 * @param personNo person number
 * @returns
 */
const validatePersonNoLength = (
  personNo: string
): {
  isVaild: boolean;
  numberLength: number;
} => {
  let response = {
    isVaild: false,
    numberLength: 0,
  };
  if (personNo.length > 11) {
    response = {
      isVaild: false,
      numberLength: personNo.length,
    };
  } else if (personNo.length === 11) {
    response = {
      isVaild: true,
      numberLength: personNo.length,
    };
  } else if (personNo.length >= 9) {
    response = {
      isVaild: true,
      numberLength: personNo.length,
    };
  } else if (personNo.length === 6) {
    response = {
      isVaild: true,
      numberLength: personNo.length,
    };
  } else {
    response = {
      isVaild: false,
      numberLength: personNo.length,
    };
  }
  return response;
};

/**
 * @function
 * @description get telephone number fields for the form
 * @param {Array<PhoneNumber>} phoneNumbers phone numbers list
 * @returns {initPhoneNumberFields} telephone fields
 */
const getPhoneNoFields = (
  phoneNumbers: Array<PhoneNumber>
): typeof initPhoneNumberFields => {
  try {
    let phoneNumberFields = { ...initPhoneNumberFields };

    phoneNumbers?.map(({ type, number }: PhoneNumber) => {
      if (Object.values(PhoneNumberTypes).includes(type)) {
        phoneNumberFields[type] = number;
      }
    });

    return phoneNumberFields;
  } catch (error) {
    return initPhoneNumberFields;
  }
};

/**
 * Swedish personal identity number validator
 */

/**
 * Normalize to 12 digits, inferring century from separator or current year.
 */
function normalizePersonnummer(input: string): string | null {
  if ((input.includes("+") || input.includes("-")) && input.length >= 12) {
    return null;
  }
  const match = input.match(/^(\d{2})(\d{2})(\d{2})([-+]?)(\d{4})$/);
  const cleaned = input.replace(/\D/g, "");

  if (match) {
    const [_, yy, mm, dd, sep, nnnn] = match;
    const year = parseInt(yy, 10);
    const now = new Date();
    const currentYear = now.getFullYear() % 100;
    const currentCentury = Math.floor(now.getFullYear() / 100);

    let century: number;

    if (sep === "+") {
      if (year <= currentYear) {
        century = currentCentury - 1;
      } else {
        century = currentCentury - 2;
      }
    } else if (year > currentYear) {
      century = currentCentury - 1;
    } else {
      century = currentCentury;
    }
    return `${century}${yy}${mm}${dd}${nnnn}`;
  }

  if (cleaned.length === 12) {
    return cleaned;
  }

  return null;
}

/**
 * Check if the birth date is valid (adjusting coordination numbers).
 */
function extractBirthDate(norm: string): {
  date: Date | null;
  isCoordination: boolean;
} {
  const year = +norm.slice(0, 4);
  const month = +norm.slice(4, 6);
  let day = +norm.slice(6, 8);
  const isCoordination = day > 60;
  if (isCoordination) day -= 60;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return { date: null, isCoordination };
  }

  return { date, isCoordination };
}

/**
 * Luhn algorithm for checksum (uses only the last 10 digits).
 */
function validateLuhn(norm: string): boolean {
  const num = norm.slice(2); // use last 10 digits
  let sum = 0;

  for (let i = 0; i < num.length - 1; i++) {
    let digit = parseInt(num[i], 10);
    if (i % 2 === 0) digit *= 2;
    if (digit > 9) digit -= 9;
    sum += digit;
  }

  const checkDigit = parseInt(num[num.length - 1], 10);
  return (sum + checkDigit) % 10 === 0;
}

/**
 * @function
 * @description Validate Swedish personal number and return structured result
 * @param {string} input - The personal number to validate
 */
export function validateSwedishNumber(input: string): {
  isValid: boolean;
  birthdate: Date | null;
} {
  const norm = normalizePersonnummer(input);
  if (!norm) return { isValid: false, birthdate: null };

  const { date, isCoordination } = extractBirthDate(norm);
  if (!date) return { isValid: false, birthdate: null };

  const isValid = validateLuhn(norm);
  return {
    isValid,
    birthdate: isValid ? date : null,
  };
}

/**
 * @function
 * @description Validates Swedish organization number (10 digits) with Luhn algorithm and specific structure
 * @param input Organization number input
 * @returns
 */

export function validateOrganizationNumber(input: string): {
  isValid: boolean;
  cleaned: string | null;
  reason?: string;
} {
  // Remove hyphen and spaces
  const cleaned = input.replace(/[-\s]/g, "");

  // 1. Format check
  if (!/^\d{10}$/.test(cleaned)) {
    return {
      isValid: false,
      cleaned: null,
      reason: "Must contain exactly 10 digits",
    };
  }

  // 2. Structure check – digits 3 and 4 (indexes 2 and 3) must be ≥ 20
  const monthLike = parseInt(cleaned.slice(2, 4), 10);
  if (monthLike < 20) {
    return {
      isValid: false,
      cleaned,
      reason:
        "Digits 3 and 4 must be 20 or greater (not a personal identity number)",
    };
  }

  // 3. Luhn checksum
  if (!luhnCheck(cleaned)) {
    return { isValid: false, cleaned, reason: "Invalid Luhn checksum" };
  }

  return { isValid: true, cleaned };
}

/**
 * @function
 * @description Luhn algorithm implementation to validate the checksum of the number
 * @param number - The organization number to validate
 * @returns
 */
function luhnCheck(number: string): boolean {
  let sum = 0;
  for (let i = 0; i < number.length - 1; i++) {
    let digit = parseInt(number[i], 10);
    if (i % 2 === 0) digit *= 2;
    if (digit > 9) digit -= 9;
    sum += digit;
  }

  const checkDigit = parseInt(number[number.length - 1], 10);
  return (sum + checkDigit) % 10 === 0;
}
